import { useState, useRef, useEffect } from "react";
import { format } from "date-fns";
import { Save, Eye, Pencil, Copy, Check, Loader2, RefreshCw, X, Sparkles, Clock, PanelRightClose, PanelRightOpen, AlertTriangle, MoreHorizontal, History, RotateCcw } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/modern/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { MarkdownRenderer } from "@/modern/components/ui/markdown-renderer";
import { Popover, PopoverTrigger, PopoverContent } from "@/modern/components/ui/popover";
import { cn } from "@/modern/utils";
import { useSMMGeneratorContext } from "../context/SMMGeneratorContext";
import { GeneratorForm } from "./GeneratorForm";
import { ManifestAuditPanel } from "./ManifestAuditPanel";
import { ManifestHistoryPanel } from "./ManifestHistoryPanel";

const COLLAPSED_WIDTH = 40;
const EXPANDED_WIDTH = 300;

function formatDate(iso: string): string {
  if (!iso?.trim()) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, "MMM d, yyyy, hh:mm a");
}

const ManifestSkeleton = () => (
  <div className="p-5 space-y-4">
    <div className="flex items-center gap-2 mb-2">
      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      <span className="text-xs text-muted-foreground">Generating manifest...</span>
    </div>
    <div className="space-y-2.5 animate-pulse">
      <div className="h-3.5 bg-muted/40 rounded w-3/4" />
      <div className="h-3.5 bg-muted/40 rounded w-full" />
      <div className="h-3.5 bg-muted/40 rounded w-5/6" />
      <div className="h-3.5 bg-muted/40 rounded w-2/3" />
      <div className="h-3" />
      <div className="h-3.5 bg-muted/40 rounded w-4/5" />
      <div className="h-3.5 bg-muted/40 rounded w-full" />
      <div className="h-3.5 bg-muted/40 rounded w-3/4" />
      <div className="h-3" />
      <div className="h-3.5 bg-muted/40 rounded w-5/6" />
      <div className="h-3.5 bg-muted/40 rounded w-1/2" />
      <div className="h-3.5 bg-muted/40 rounded w-full" />
      <div className="h-3.5 bg-muted/40 rounded w-2/3" />
    </div>
  </div>
);

export const ManifestEditor = () => {
  const {
    editedContent,
    handleContentChange,
    handleSave,
    handleRevert,
    isSaving,
    isDirty,
    isGenerating,
    hasOpenAIKey,
    confidenceScores,
    history,
    cancelGeneration,
    previewEntry,
    previewContent,
    clearHistoryPreview,
    revertingId,
  } = useSMMGeneratorContext();

  const [isPreview, setIsPreview] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRegenForm, setShowRegenForm] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarTab, setSidebarTab] = useState("audit");
  const [toolbarCompact, setToolbarCompact] = useState(false);
  const [toolbarPopoverOpen, setToolbarPopoverOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const toolbarActionsRef = useRef<HTMLDivElement>(null);

  const overallScore = confidenceScores?.overall != null ? Math.round(confidenceScores.overall * 100) : null;

  const lowSectionCount = confidenceScores
    ? Object.entries(confidenceScores)
        .filter(([key, val]) => key !== "overall" && val?.score != null && val.score < 0.6).length
    : 0;

  useEffect(() => {
    if (isPreview || isGenerating) return;
    const ta = textareaRef.current;
    if (!ta) return;
    const scrollParent = ta.parentElement;
    const prevScrollTop = scrollParent?.scrollTop ?? 0;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
    if (scrollParent) {
      scrollParent.scrollTop = prevScrollTop;
    }
  }, [editedContent, isPreview, isGenerating]);

  // Title + sidebar toggle reserved space (~180px)
  const TOOLBAR_RESERVED_PX = 180;

  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;
    const observer = new ResizeObserver(() => {
      const actions = toolbarActionsRef.current;
      if (!actions || !toolbar) return;
      setToolbarCompact(actions.scrollWidth + TOOLBAR_RESERVED_PX > toolbar.clientWidth);
    });
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, []);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(editedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex h-full min-h-0">

      {/* Editor pane */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        {/* Toolbar */}
        <div ref={toolbarRef} className="flex items-center justify-between gap-2 px-4 h-10 shrink-0 border-b border-border">
          <div className="flex items-center gap-2 min-w-0 shrink-0">
            <span className="text-sm font-semibold text-foreground whitespace-nowrap">Manifest File</span>
            {isDirty && !isGenerating && !previewEntry && (
              <span className="text-[10px] font-medium text-amber-500 bg-amber-500/10 rounded px-1.5 py-0.5">
                Unsaved
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {/* Full action buttons — always in DOM for measurement; hidden when compact */}
            <div
              ref={toolbarActionsRef}
              className={cn(
                "flex items-center gap-1",
                toolbarCompact && "pointer-events-none invisible absolute",
              )}
              aria-hidden={toolbarCompact}
            >
              {isGenerating ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1.5 px-2 text-xs text-destructive hover:text-destructive"
                      onClick={cancelGeneration}
                      tabIndex={toolbarCompact ? -1 : 0}
                    >
                      <X className="h-3.5 w-3.5" />
                      Cancel
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Cancel manifest generation</TooltipContent>
                </Tooltip>
              ) : showRegenForm ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setShowRegenForm(false)}
                      tabIndex={toolbarCompact ? -1 : 0}
                    >
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              ) : (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1.5 px-2 text-xs"
                        onClick={() => setShowRegenForm(true)}
                        disabled={!hasOpenAIKey || !!previewEntry}
                        title={!hasOpenAIKey ? "An OpenAI API key is required" : undefined}
                        tabIndex={toolbarCompact ? -1 : 0}
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Re-Generate
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Generate a new manifest from your business details</TooltipContent>
                  </Tooltip>

                  <div className="w-px h-4 bg-border mx-0.5" />

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1.5 px-2 text-xs"
                        onClick={() => setIsPreview(!isPreview)}
                        disabled={!!previewEntry}
                        tabIndex={toolbarCompact ? -1 : 0}
                      >
                        {isPreview ? <Pencil className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        {isPreview ? "Edit" : "Preview"}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{isPreview ? "Switch to edit mode" : "Preview rendered markdown"}</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1.5 px-2 text-xs"
                        onClick={handleCopy}
                        disabled={!!previewEntry}
                        tabIndex={toolbarCompact ? -1 : 0}
                      >
                        {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                        {copied ? "Copied" : "Copy"}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy manifest to clipboard</TooltipContent>
                  </Tooltip>

                  <Button
                    variant={isDirty ? "default" : "ghost"}
                    size="sm"
                    className="h-7 gap-1.5 px-2 text-xs"
                    onClick={handleSave}
                    disabled={isSaving || !isDirty || !!previewEntry}
                    tabIndex={toolbarCompact ? -1 : 0}
                  >
                    {isSaving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5" />
                    )}
                    Save
                  </Button>
                </>
              )}
            </div>

            {/* Compact popover — visible when toolbar overflows */}
            {toolbarCompact && (
              <Popover open={toolbarPopoverOpen} onOpenChange={setToolbarPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0"
                    aria-label="More actions"
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={6} className="w-44 p-1.5">
                  <div className="flex flex-col gap-0.5">
                    {isGenerating ? (
                      <button
                        onClick={() => { cancelGeneration(); setToolbarPopoverOpen(false); }}
                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
                      >
                        <X className="h-3.5 w-3.5 shrink-0" />
                        Cancel
                      </button>
                    ) : showRegenForm ? (
                      <button
                        onClick={() => { setShowRegenForm(false); setToolbarPopoverOpen(false); }}
                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
                      >
                        <X className="h-3.5 w-3.5 shrink-0" />
                        Close form
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => { setShowRegenForm(true); setToolbarPopoverOpen(false); }}
                          disabled={!hasOpenAIKey || !!previewEntry}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium transition-colors",
                            !hasOpenAIKey || !!previewEntry
                              ? "text-muted-foreground/50 cursor-not-allowed"
                              : "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
                          )}
                        >
                          <RefreshCw className="h-3.5 w-3.5 shrink-0" />
                          Re-Generate
                        </button>
                        <button
                          onClick={() => { if (!previewEntry) { setIsPreview(!isPreview); setToolbarPopoverOpen(false); } }}
                          disabled={!!previewEntry}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium transition-colors",
                            previewEntry
                              ? "text-muted-foreground/50 cursor-not-allowed"
                              : "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
                          )}
                        >
                          {isPreview ? <Pencil className="h-3.5 w-3.5 shrink-0" /> : <Eye className="h-3.5 w-3.5 shrink-0" />}
                          {isPreview ? "Edit" : "Preview"}
                        </button>
                        <button
                          onClick={() => { if (!previewEntry) { handleCopy(); setToolbarPopoverOpen(false); } }}
                          disabled={!!previewEntry}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium transition-colors",
                            previewEntry
                              ? "text-muted-foreground/50 cursor-not-allowed"
                              : "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
                          )}
                        >
                          {copied ? <Check className="h-3.5 w-3.5 shrink-0 text-success" /> : <Copy className="h-3.5 w-3.5 shrink-0" />}
                          {copied ? "Copied" : "Copy"}
                        </button>
                        <button
                          onClick={() => { handleSave(); setToolbarPopoverOpen(false); }}
                          disabled={isSaving || !isDirty || !!previewEntry}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium transition-colors",
                            isSaving || !isDirty || !!previewEntry
                              ? "text-muted-foreground/50 cursor-not-allowed"
                              : "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
                          )}
                        >
                          {isSaving ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> : <Save className="h-3.5 w-3.5 shrink-0" />}
                          Save
                        </button>
                      </>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            <div className="w-px h-4 bg-border mx-0.5" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  {sidebarOpen ? (
                    <PanelRightClose className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <PanelRightOpen className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{sidebarOpen ? "Collapse panel" : "Expand panel"}</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* History version preview banner */}
        <AnimatePresence>
          {previewEntry && (
            <motion.div
              key="history-preview-banner"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="overflow-hidden border-b border-border shrink-0"
            >
              <div className="flex items-center justify-between gap-4 px-5 py-3 bg-muted/20">
                <div className="flex items-center gap-2 min-w-0 text-xs text-muted-foreground">
                  <History className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    Previewing version from{" "}
                    <span className="font-medium text-foreground">{formatDate(previewEntry.created_at)}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={clearHistoryPreview}
                  >
                    Back to current
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 gap-1.5 px-2 text-xs"
                    disabled={revertingId === previewEntry.id}
                    onClick={async () => {
                      await handleRevert(previewEntry.id);
                      clearHistoryPreview();
                    }}
                  >
                    {revertingId === previewEntry.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RotateCcw className="h-3 w-3" />
                    )}
                    Revert to this version
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Inline re-generate form */}
        <AnimatePresence>
          {showRegenForm && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="overflow-hidden border-b border-border shrink-0"
            >
              <div className="px-5 py-4">
                <GeneratorForm compact onGenerated={() => setShowRegenForm(false)} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Editor content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {previewEntry ? (
            <div className="p-5 opacity-90">
              <MarkdownRenderer content={previewContent ?? ""} />
            </div>
          ) : isGenerating ? (
            <ManifestSkeleton />
          ) : isPreview ? (
            <div className="p-5">
              <MarkdownRenderer content={editedContent} />
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              value={editedContent}
              onChange={(e) => handleContentChange(e.target.value)}
              className={cn(
                "w-full min-h-full p-5 bg-transparent text-sm text-foreground",
                "font-mono leading-relaxed resize-none",
                "focus:outline-none",
                "placeholder:text-muted-foreground"
              )}
              placeholder="Manifest content will appear here..."
              spellCheck={false}
            />
          )}
        </div>
      </div>

      {/* Sidebar: expanded or collapsed strip */}
      <motion.div
        initial={false}
        animate={{ width: sidebarOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="shrink-0 border-l border-border flex flex-col min-h-0 overflow-hidden"
      >
        <AnimatePresence mode="wait">
          {sidebarOpen ? (
            <motion.div
              key="expanded"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
              className="flex flex-col h-full min-h-0"
            >
              <Tabs value={sidebarTab} onValueChange={setSidebarTab} className="flex flex-col h-full">
                <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent h-10 px-1 shrink-0">
                  <TabsTrigger
                    value="audit"
                    className="rounded-none px-2 py-1.5 text-xs data-[state=active]:shadow-none"
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    Audit
                    {!isGenerating && overallScore != null && (
                      <span className="ml-1 text-[10px] font-semibold text-muted-foreground">
                        {overallScore}%
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="history"
                    className="rounded-none px-2 py-1.5 text-xs data-[state=active]:shadow-none"
                  >
                    <Clock className="h-3 w-3 mr-1" />
                    History
                    {history.length > 0 && (
                      <span className="ml-1 text-[10px] font-semibold text-muted-foreground">
                        ({history.length})
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="audit" className="flex-1 min-h-0 overflow-y-auto mt-0">
                  <ManifestAuditPanel />
                </TabsContent>

                <TabsContent value="history" className="flex-1 min-h-0 overflow-y-auto mt-0">
                  <ManifestHistoryPanel />
                </TabsContent>
              </Tabs>
            </motion.div>
          ) : (
            <motion.div
              key="collapsed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
              className="flex flex-col items-center py-3 gap-4 h-full"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => { setSidebarTab("audit"); setSidebarOpen(true); }}
                    className={cn(
                      "flex flex-col items-center gap-1.5 p-1.5 rounded-md transition-colors",
                      "hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Sparkles className="h-4 w-4" />
                    {!isGenerating && overallScore != null && (
                      <span className="text-[10px] font-semibold text-primary leading-none">{overallScore}%</span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left">Audit scores</TooltipContent>
              </Tooltip>

              {lowSectionCount > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => { setSidebarTab("audit"); setSidebarOpen(true); }}
                      className="flex items-center justify-center p-1.5 rounded-md text-amber-500 hover:bg-amber-500/10 transition-colors"
                    >
                      <AlertTriangle className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">{lowSectionCount} section{lowSectionCount > 1 ? "s" : ""} below 60%</TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => { setSidebarTab("history"); setSidebarOpen(true); }}
                    className={cn(
                      "flex flex-col items-center gap-1.5 p-1.5 rounded-md transition-colors",
                      "hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Clock className="h-4 w-4" />
                    {history.length > 0 && (
                      <span className="text-[10px] font-semibold text-muted-foreground leading-none">{history.length}</span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left">History ({history.length})</TooltipContent>
              </Tooltip>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

    </div>
  );
};
